import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { NetworkContext } from '../../context/NetworkContext';
import { SetupContext } from '../../context/SetupContext';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';

import { Check } from 'lucide-react';

import CustomButton from '../Componentization/CustomButton';
import CustomText from '../../styles/CustomText';
import { useNavigate, useLocation } from 'react-router-dom';
import useQssMnemonic from '../../hooks/useQssMnemonic';
import { useTranslation } from 'react-i18next';
import useSecureStorage from '../../hooks/useSecureStorage';

import WebApp from '@twa-dev/sdk'

interface Props {
    // 필요한 prop 타입 정의 (필요에 따라 추가)
}

const CreateWallet: React.FC<Props> = () => {
    const { theme } = useContext(ThemeContext)
    let location = useLocation();
    let navigate = useNavigate();
    const { t } = useTranslation();

    const networkContext = useContext(NetworkContext);
    const setupContext = useContext(SetupContext);
    const defaultTokenContext = useContext(DefaultTokenContext);

    const { checkEmptyQssMnemonic, checkOldQssData, getQssMnemonicHash } = useQssMnemonic()
    const { getBlockChain } = useSecureStorage()

    const [isSelected, setSelection] = useState(false)
    const [isQssMnemonicAvailable, setIsQssMnemonicAvailable] = useState(false)
    const [isNewQssMnemonicAvailable, setIsNewQssMnemonicAvailable] = useState(true)

    useEffect(() => {
        const initializeTgBack = async () => {
            // QSS 니모닉 데이터 존재 여부 확인
            checkQssMnemonic()

            const fullPath = location.pathname + location.hash
            console.log(`텔레그램 백버튼 초기화 : ${fullPath}`)

            const platform = WebApp.platform
            if (fullPath === '/CreateWallet') {
                // CreateWallet으로 들어오면 텔레그램 백 버튼 숨김 (닫기 버튼으로 변경됨)
                if ('android' === platform || 
                    'android_x' === platform || 
                    'ios' === WebApp.platform || 
                    'tdesktop' === WebApp.platform || 
                    'macos' === WebApp.platform) {
                    WebApp.BackButton.hide();
                }
            } else {
                // 텔레그램 미니앱의 백 버튼 표시
                if ('android' === platform || 
                    'android_x' === platform || 
                    'ios' === WebApp.platform || 
                    'tdesktop' === WebApp.platform ||
                    'macos' === WebApp.platform) {
                    WebApp.BackButton.show();
                }
            }

            // 기본 네트워크, 토큰 정보 갱신
            await defaultTokenContext.forceTokenDataUpdate();

            // 이벤트 정보 갱신
            await setupContext.forceEventUpdate();
        };

        initializeTgBack()
    }, []);

    const manualWallet = async () => {
        navigate(`/CreateManualWallet`);
    }

    const createQssWallet = async () => {
        navigate(`/QssCreateWalletProtection`);
    }

    const recoveryQssWallet = async () => {
        navigate(`/WalletQssRecovery`);
    }

    /**
     * QSS 니모닉 데이터 존재 여부 확인
     * @returns 
     */
    const checkQssMnemonic = async (): Promise<void> => {
		try {
            const selNetwork = await getBlockChain();

            // NetworkContext의 setSelectedNetwork 호출해서 교체
			await networkContext.setSelectedNetwork(selNetwork.toLowerCase());

            // qss에 저장된 니모닉 데이터 있는지 확인
			const isEmpty = await checkEmptyQssMnemonic(selNetwork);
			if(true === isEmpty) {
				// 이전 저장된 qss 니모닉 데이터 없음
				// WebApp.showAlert(t('wallet_qss_recovery_message_13'));
                console.warn(`checkQssMnemonic 저장된 QSS 니모닉 데이터 없음`)
				return
			}

			// 이전 형식의 QSS 데이터 인지 확인
			const isOldData = await checkOldQssData(selNetwork);
			if (true === isOldData) {
				// QSS 데이터 형식이 호환되지 않는 이전 형식
				WebApp.showAlert(t('wallet_qss_recovery_message_14'));
				return
			}

            console.log(`checkQssMnemonic 저장된 QSS 니모닉 데이터 있음`)
			// 저장된 qss 니모닉 데이터 있음
            // QSS에서 지갑을 복구 버튼 활성상태 설정
			setIsQssMnemonicAvailable(true)

            // 저장된 QSS 니모닉코드 해시
            const qssMnemonicHash = await getQssMnemonicHash(selNetwork)

			if(qssMnemonicHash  && true === qssMnemonicHash?.result) {
                // 현재 저장된 QSS 니모닉 수량 확인
                const maxQssMnemonicCount = Number(process.env.REACT_APP_QSS_MNEMONIC_MAX_COUNT) || 9;
                if(null != qssMnemonicHash.value) {
					if( maxQssMnemonicCount <= qssMnemonicHash.value.length ) {
                        // 현재 저장된 QSS 니모닉 수량 제한 초과
                        setIsNewQssMnemonicAvailable(false)
                    }
                }
            }
		} catch (error) {
			console.warn('Error handling QSS load:', error);
		}
	};

    const handleTermsLink = async () => {
        let url = setupContext.commonInfo?.term_url || ''
        try {
            await window.open(url, '_blank');
        } catch (error) {
            console.log('Failed to open Terms URL: ', error)
        }
    }

    const styles = {
        container: {
            paddingTop: "24px"
        } as React.CSSProperties,
        header: {
            width: "100%"
        } as React.CSSProperties,
        title: {
            fontSize: '24px', // px 단위 추가
            textAlign: 'left',
        } as React.CSSProperties,
        subtitle: {
            fontSize: '16px',
            textAlign: 'left',
            marginTop: '15px',
        } as React.CSSProperties,
        imageContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        } as React.CSSProperties,
        image: {
            width: '180px',
            height: '180px',
        } as React.CSSProperties,
        footer: {
            width: "100%"
        } as React.CSSProperties,
        checkboxContainer: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
            width: '100%',
        } as React.CSSProperties,
        checkboxBase: {
            width: '22px',
            height: '22px',
            borderRadius: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '5px',
        } as React.CSSProperties,
        label: {
            fontSize: '16px',
            display: 'flex',
            gap: '5px'
        } as React.CSSProperties,
        underline: {
            textDecoration: 'underline', // React 웹에서는 textDecorationLine 대신 textDecoration 사용
        } as React.CSSProperties,
        buttonPrimary: {
            padding: '12px',
            borderRadius: '10px',
            marginBottom: '12px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            height: '54px',
            justifyContent: 'center',
        } as React.CSSProperties,
        buttonPrimaryText: {
            fontSize: '16px',
            textAlign: 'center', // textAlignVertical은 웹에서는 textAlign으로 처리
            color: theme.static_white
        } as React.CSSProperties,
        buttonSecondary: {
            padding: '12px',
            borderRadius: '10px',
            marginBottom: '12px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            height: '54px',
            justifyContent: 'center',
        } as React.CSSProperties,
        buttonSecondaryText: {
            fontSize: '16px',
            textAlign: 'center', // textAlignVertical을 대체
            color: theme.label_strong
        } as React.CSSProperties,
        divider: {
            width: 1,
            height: 30,  
        } as React.CSSProperties,
    };

    return (
        <div className='container' style={{ ...styles.container, backgroundColor: theme.background_normal }}>
            <div style={styles.header}>
                <CustomText weight="medium" style={{ ...styles.title, color: theme.label_strong }}>
                    {t('main_message_01')}
                </CustomText>
                <CustomText weight="medium" style={{ ...styles.title, color: theme.label_strong }}>
                    {t('main_message_02')}
                </CustomText>
                <CustomText style={{ ...styles.subtitle, color: theme.label_alternative }}>
                    {t('main_message_03')}
                </CustomText>
            </div>
            <div style={styles.imageContainer}>
                <img
                    src={`./img/login_wallet_icon.png`} // process.env.PUBLIC_URL로 경로 설정
                    alt="Login Wallet"
                    style={{ ...styles.image, objectFit: 'contain' }} // objectFit으로 이미지 크기 조정
                />
            </div>
            <div style={styles.footer}>
                <div style={styles.checkboxContainer}>
                    <div
                        onClick={() => setSelection(!isSelected)}
                        style={{
                            ...styles.checkboxBase,
                            backgroundColor: isSelected
                                ? theme.accent_lime
                                : theme.fill_normal,
                            border: `1px solid ${isSelected ? theme.accent_lime : theme.label_alternative}`,
                            cursor: 'pointer', // React 웹에서는 onClick에 cursor 스타일이 필요
                        }}
                    >
                        {isSelected && <Check style={{ width: "18px", color: theme.static_white }} />}
                    </div>
                    <CustomText style={{ ...styles.label, color: theme.label_normal }}>
                        <CustomText
                            weight="medium"
                            style={{ ...styles.underline, color: theme.label_normal }}
                            onClick={() => { handleTermsLink() }} // React 웹에서는 onPress 대신 onClick 사용
                        >
                            {t('main_message_04')}
                        </CustomText>
                        {t('main_message_05')}
                    </CustomText>
                </div>
                <div style={{
                    height: '200px', // 기본 높이 설정
                    opacity: isSelected ? 1 : 0,
                    visibility: isSelected ? 'visible' : 'hidden',
                }}>
                    <CustomButton
                        containerStyle={{
                            ...styles.buttonPrimary, // 기존 스타일
                        }}
                        textStyle={{ ...styles.buttonPrimaryText }}
                        onPress={() => { recoveryQssWallet() }}
                        text={t('main_message_09')} 
                        disabled={!isQssMnemonicAvailable}
                        disabledStyle={isQssMnemonicAvailable ? { color: theme.label_strong } : { backgroundColor: theme.primary_normal_disable, }}
                        withBackground={true}
                        backgroundColor={theme.primary_normal}
                    />
                    <CustomButton
                        containerStyle={{
                            ...styles.buttonPrimary, // 기존 스타일
                        }}
                        textStyle={{ ...styles.buttonPrimaryText }}
                        onPress={() => { createQssWallet() }}
                        text={isNewQssMnemonicAvailable ? t('main_message_10') : t('main_message_12')}
                        disabled={!isNewQssMnemonicAvailable}
                        disabledStyle={isNewQssMnemonicAvailable ? { color: theme.label_strong } : { backgroundColor: theme.primary_normal_disable, }}
                        withBackground={true}
                        backgroundColor={theme.primary_normal}
                    />
                    <div style={styles.divider}></div>
                    <CustomButton
                        containerStyle={{
                            ...styles.buttonSecondary, // 기존 스타일
                        }}
                        textStyle={{ ...styles.buttonSecondaryText, }}
                        onPress={() => { manualWallet() }}
                        text={t('main_message_11')}
                        disabled={false}
                        disabledStyle={{ color: theme.label_disable }}
                        withBackground={true}
                        backgroundColor={theme.fill_alternative}
                    />
                </div>

            </div>
        </div>
    )
}

export default CreateWallet
